import React from 'react';
import {
  useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedTab as Tab, LocalizedTableCell as TableCell } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { useNavigate } from 'react-router-dom';
import AnalysisTabBar from '../components/AnalysisTabBar';
import SeoHead from '../components/seo/SeoHead';
import { useAuth } from '../contexts/AuthContext';
import { useChart } from '../contexts/ChartContext';
import { useCurrentTransitOverlay } from '../hooks/useCurrentTransitOverlay';
import { hasPremiumSubscription } from '../utils/subscription';
import { formatDisplayDate } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type WesternTab = 'natal' | 'transits' | 'solarReturn' | 'synastry' | 'progressions';

const WESTERN_TABS: { key: WesternTab; label: string; premium?: boolean }[] = [
  { key: 'natal', label: 'Natal Western Chart' },
  { key: 'transits', label: 'Transits', premium: true },
  { key: 'solarReturn', label: 'Solar Return', premium: true },
  { key: 'synastry', label: 'Synastry', premium: true },
  { key: 'progressions', label: 'Life Phase Progression', premium: true },
];

const PLANET_COLORS: Record<string, string> = {
  SUN: '#dc2626',
  MOON: '#7c3aed',
  MARS: '#ef4444',
  MERCURY: '#16a34a',
  JUPITER: '#d97706',
  VENUS: '#0f766e',
  SATURN: '#4f46e5',
  URANUS: '#0891b2',
  NEPTUNE: '#2563eb',
  PLUTO: '#7c2d12',
  RAHU: '#111827',
  KETU: '#92400e',
};

const PLANET_ABBR: Record<string, string> = {
  SUN: 'Su',
  MOON: 'Mo',
  MARS: 'Ma',
  MERCURY: 'Me',
  JUPITER: 'Ju',
  VENUS: 'Ve',
  SATURN: 'Sa',
  URANUS: 'Ur',
  NEPTUNE: 'Ne',
  PLUTO: 'Pl',
  RAHU: 'Ra',
  KETU: 'Ke',
};

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const SIGN_GLYPHS = ['Ar', 'Ta', 'Ge', 'Ca', 'Le', 'Vi', 'Li', 'Sc', 'Sg', 'Cp', 'Aq', 'Pi'];

const WESTERN_ASPECTS = [
  { name: 'Conjunction', angle: 0, orb: 8, nature: 'Powerful' },
  { name: 'Sextile', angle: 60, orb: 4, nature: 'Supportive' },
  { name: 'Square', angle: 90, orb: 6, nature: 'Challenging' },
  { name: 'Trine', angle: 120, orb: 6, nature: 'Supportive' },
  { name: 'Opposition', angle: 180, orb: 8, nature: 'Challenging' },
];

const polarPoint = (cx: number, cy: number, radius: number, angleDeg: number) => {
  const radians = (angleDeg - 90) * Math.PI / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const formatDegree = (value: any) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(2)} deg` : '-';
};

const formatSignDegree = (item: any) => {
  const sign = item?.sign?.name || '-';
  const degree = item?.degree?.formatted || formatDegree(item?.sign?.degrees ?? item?.degreeInSign);
  return `${sign} ${degree}`;
};

const normalizePlanetRows = (chart: any) =>
  Object.entries(chart?.planets || {})
    .map(([key, planet]: [string, any]) => {
      const longitude = Number(planet?.longitude);
      return {
        key,
        name: planet?.name || key,
        signDegree: formatSignDegree(planet),
        house: planet?.bhavaChalitHouse || planet?.house || '-',
        longitude,
        longitudeText: formatDegree(planet?.longitude),
        retrograde: Boolean(planet?.retrograde || planet?.isRetrograde || Number(planet?.speed) < 0),
      };
    })
    .filter((planet) => Number.isFinite(planet.longitude))
    .sort((a, b) => a.longitude - b.longitude);

const normalizeCuspRows = (chart: any) =>
  Object.entries(chart?.houses?.cusps || {})
    .map(([house, cusp]: [string, any]) => ({
      house,
      signDegree: formatSignDegree(cusp),
      longitude: Number(cusp?.longitude),
      longitudeText: formatDegree(cusp?.longitude),
      ruler: cusp?.sign?.ruler || '-',
    }))
    .filter((cusp) => Number.isFinite(cusp.longitude))
    .sort((a, b) => Number(a.house) - Number(b.house));

const angularDistance = (a: number, b: number) => {
  const diff = Math.abs((((a - b) % 360) + 540) % 360 - 180);
  return Number.isFinite(diff) ? diff : 0;
};

const aspectStrength = (orb: number) => {
  if (orb <= 1) return 'Exact';
  if (orb <= 3) return 'Strong';
  return 'Moderate';
};

const calculateWesternAspects = (primaryPoints: any[], secondaryPoints = primaryPoints, sameSet = true, maxRows = 28) => {
  const rows: any[] = [];

  primaryPoints.forEach((first, firstIndex) => {
    secondaryPoints.forEach((second, secondIndex) => {
      if (sameSet && secondIndex <= firstIndex) return;
      if (!sameSet && first.key === second.key && first.source === second.source) return;
      if (!Number.isFinite(first.longitude) || !Number.isFinite(second.longitude)) return;

      const distance = angularDistance(first.longitude, second.longitude);
      const match = WESTERN_ASPECTS
        .map((aspect) => ({ ...aspect, orbDelta: Math.abs(distance - aspect.angle) }))
        .filter((aspect) => aspect.orbDelta <= aspect.orb)
        .sort((a, b) => a.orbDelta - b.orbDelta)[0];

      if (!match) return;

      rows.push({
        id: `${first.source || 'N'}-${first.key}-${second.source || 'N'}-${second.key}-${match.name}`,
        pointA: first.displayName || first.name,
        pointB: second.displayName || second.name,
        aspect: match.name,
        nature: match.nature,
        distance,
        orb: match.orbDelta,
        strength: aspectStrength(match.orbDelta),
      });
    });
  });

  return rows.sort((a, b) => a.orb - b.orb).slice(0, maxRows);
};

const labelPointSet = (points: any[], prefix: string) =>
  points.map((point) => ({
    ...point,
    source: prefix,
    displayName: prefix ? `${prefix} ${point.name}` : point.name,
  }));

const relationshipPoints = (aspects: any[]) => {
  const focus = new Set(['SUN', 'MOON', 'VENUS', 'MARS']);
  return aspects.filter((aspect) => {
    const text = `${aspect.pointA} ${aspect.pointB}`.toUpperCase();
    return Array.from(focus).some((key) => text.includes(key) || text.includes(key.charAt(0) + key.slice(1).toLowerCase()));
  });
};

const formatSolarReturnDate = (chart: any) => {
  const raw = chart?.birthDetails?.dateOfBirth || chart?.dateOfBirth || chart?.dob;
  if (!raw) return `${new Date().getFullYear()} solar year`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return `${new Date().getFullYear()} solar year`;
  const returnDate = new Date(new Date().getFullYear(), date.getMonth(), date.getDate());
  return formatDisplayDate(returnDate);
};

const pad2 = (value: number) => String(value).padStart(2, '0');

const timeOfBirthToClock = (value: any) => {
  if (!value) return '12:00';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    return match ? `${pad2(Number(match[1]))}:${match[2]}` : '12:00';
  }
  const hour = Number(value.hour ?? value.hours ?? value.hh);
  const minute = Number(value.minute ?? value.minutes ?? value.mm);
  if (Number.isFinite(hour) && Number.isFinite(minute)) return `${pad2(hour)}:${pad2(minute)}`;
  return '12:00';
};

const getSolarReturnTarget = (chart: any) => {
  const raw = chart?.birthDetails?.dateOfBirth || chart?.dateOfBirth || chart?.dob;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const year = new Date().getFullYear();
  return {
    date: `${year}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`,
    time: timeOfBirthToClock(chart?.birthDetails?.timeOfBirth || chart?.timeOfBirth),
  };
};

const getDateOnly = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const timezoneToMinutes = (value: any) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (((Number(match[2]) || 0) * 60) + (Number(match[3]) || 0));
};

const localDateTimeToUtc = (dateText: string, timeText: string, timezoneMinutes: number) => {
  const [year, month, day] = dateText.split('-').map(Number);
  const [hour, minute] = timeText.split(':').map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour || 0, minute || 0, 0) - timezoneMinutes * 60000);
};

const getProgressionTarget = (chart: any) => {
  const birthDate = getDateOnly(chart?.birthDetails?.dateOfBirth || chart?.dateOfBirth || chart?.dob);
  if (!birthDate) return null;
  const birthTime = timeOfBirthToClock(chart?.birthDetails?.timeOfBirth || chart?.timeOfBirth);
  const place = chart?.birthDetails?.placeOfBirth || {};
  const timezoneMinutes = timezoneToMinutes(place.timezone ?? place.timezoneOffset);
  const birthUTC = localDateTimeToUtc(birthDate, birthTime, timezoneMinutes);
  const targetUTC = new Date();
  const ageYears = (targetUTC.getTime() - birthUTC.getTime()) / (365.2425 * 24 * 60 * 60 * 1000);
  const progressedDate = new Date(birthUTC.getTime() + ageYears * 24 * 60 * 60 * 1000);
  return {
    date: `${progressedDate.getUTCFullYear()}-${pad2(progressedDate.getUTCMonth() + 1)}-${pad2(progressedDate.getUTCDate())}`,
    time: `${pad2(progressedDate.getUTCHours())}:${pad2(progressedDate.getUTCMinutes())}`,
    ageYears,
    progressedDate,
  };
};

const progressedHouseMeaning = (planet: string, house: any) => {
  const numericHouse = Number(house);
  const moonMeanings: Record<number, string> = {
    1: 'Self-focus, identity change, body awareness and personal reset.',
    2: 'Money, values, family, speech and security become emotionally important.',
    3: 'Communication, learning, siblings, writing and short travel become important.',
    4: 'Home, mother, property, family and emotional roots become important.',
    5: 'Creativity, romance, children, hobbies and self-expression become important.',
    6: 'Work routine, health habits, service and discipline become important.',
    7: 'Relationship, marriage, partnership and public dealing become important.',
    8: 'Deep emotional change, intimacy, shared money and transformation become important.',
    9: 'Learning, travel, teaching, belief and higher wisdom become important.',
    10: 'Career, status, public image and responsibility become emotionally important.',
    11: 'Income, friends, networking, community and ambitions become important.',
    12: 'Rest, isolation, spirituality, foreign connection and inner healing become important.',
  };
  const sunMeanings: Record<number, string> = {
    1: 'Identity development and self-expression are central.',
    2: 'Values, money and self-worth become central.',
    3: 'Communication, learning and skills become central.',
    4: 'Home, roots and emotional foundation become central.',
    5: 'Creativity, love and self-expression become central.',
    6: 'Work, discipline and health routines become central.',
    7: 'Partnerships and relationship identity become central.',
    8: 'Transformation, deep healing and shared resources become central.',
    9: 'Wisdom, travel, belief and higher learning become central.',
    10: 'Career, authority and public role become central.',
    11: 'Community, income, goals and networks become central.',
    12: 'Spirituality, closure, retreat and inner development become central.',
  };
  if (planet === 'MOON') return moonMeanings[numericHouse] || 'This house becomes emotionally important.';
  if (planet === 'SUN') return sunMeanings[numericHouse] || 'This house becomes central for identity development.';
  if (planet === 'MERCURY') return 'Thinking, communication, learning and decisions are developing through this house.';
  if (planet === 'VENUS') return 'Love, comfort, values, beauty and money preferences are developing through this house.';
  if (planet === 'MARS') return 'Action style, courage, ambition and drive are developing through this house.';
  if (planet === 'JUPITER') return 'Growth, faith, learning and expansion themes develop through this house.';
  if (planet === 'SATURN') return 'Maturity, responsibility, discipline and long-term structure develop through this house.';
  return 'This progressed planet shows an inner life phase through this house.';
};

const getProgressedMoonPhase = (progressedPlanets: any[]) => {
  const sun = progressedPlanets.find((planet) => planet.key === 'SUN');
  const moon = progressedPlanets.find((planet) => planet.key === 'MOON');
  if (!sun || !moon) return null;
  const angle = (moon.longitude - sun.longitude + 360) % 360;
  if (angle < 45) return { phase: 'New Moon Phase', angle, meaning: 'New beginning, identity reset and fresh internal cycle.' };
  if (angle < 90) return { phase: 'Crescent Phase', angle, meaning: 'Growth, effort, learning and building confidence.' };
  if (angle < 135) return { phase: 'First Quarter Phase', angle, meaning: 'Action, decision-making, pressure and development.' };
  if (angle < 180) return { phase: 'Gibbous Phase', angle, meaning: 'Refinement, improvement, preparation and skill-building.' };
  if (angle < 225) return { phase: 'Full Moon Phase', angle, meaning: 'Visibility, relationship awareness, emotional clarity and results.' };
  if (angle < 270) return { phase: 'Disseminating Phase', angle, meaning: 'Sharing, teaching, communication and distributing experience.' };
  if (angle < 315) return { phase: 'Last Quarter Phase', angle, meaning: 'Reorientation, correction, release and mature decision-making.' };
  return { phase: 'Balsamic Phase', angle, meaning: 'Closure, spiritual reflection, rest and preparation for a new cycle.' };
};

const progressedAspectInterpretation = (progressedPlanet: string, natalPlanet: string, aspect: string) => {
  const key = `${progressedPlanet}_${aspect}_${natalPlanet}`;
  const meanings: Record<string, string> = {
    MOON_Conjunction_SUN: 'A major emotional and identity phase begins. The person may feel ready for a new chapter.',
    MOON_Conjunction_MOON: 'Emotional reset period. Family, comfort, inner peace and emotional needs become important.',
    MOON_Conjunction_VENUS: 'Love, relationship, comfort, beauty and emotional bonding become important.',
    MOON_Square_VENUS: 'Emotional needs and relationship expectations may need adjustment.',
    MOON_Conjunction_MARS: 'Energy, passion, impatience and action increase. Emotional reactions may become quick.',
    MOON_Square_MARS: 'Emotional restlessness, anger, conflict or urgent action can increase.',
    MOON_Conjunction_SATURN: 'A serious emotional phase. Responsibility, maturity, family pressure or inner discipline may increase.',
    MOON_Square_SATURN: 'Emotional heaviness or responsibility may be felt. Patience and maturity are required.',
    MOON_Trine_JUPITER: 'Supportive emotional growth, optimism, travel, learning and blessings can increase.',
    MOON_Conjunction_JUPITER: 'A growth-oriented emotional phase. Good for learning, expansion, support and positive decisions.',
    SUN_Conjunction_VENUS: 'Identity, love, beauty, money values and creative expression become highlighted.',
    SUN_Conjunction_MARS: 'Confidence, courage, ambition and action increase. Good for starting a new active phase.',
    SUN_Square_SATURN: 'A maturity test. Responsibility, delay, pressure or career restructuring may occur.',
    SUN_Trine_JUPITER: 'Growth, confidence, wisdom, recognition and opportunity can increase.',
    MERCURY_Conjunction_SUN: 'Mental clarity, communication, study, writing and business decisions become important.',
    MERCURY_Conjunction_MERCURY: 'A new mental cycle begins. Good for learning, communication, writing and skill development.',
    VENUS_Conjunction_SUN: 'Love, beauty, comfort, social life and values become strongly connected to identity.',
    MARS_Conjunction_SUN: 'Action, confidence, ambition and personal drive become stronger.',
  };
  return meanings[key] || `Progressed ${progressedPlanet} ${aspect.toLowerCase()} natal ${natalPlanet} shows an internal life phase where ${progressedPlanet} themes activate natal ${natalPlanet} themes.`;
};

const calculateProgressedAspects = (progressedPlanets: any[], natalPlanets: any[]) => {
  const progressedKeys = new Set(['SUN', 'MOON', 'MERCURY', 'VENUS', 'MARS', 'JUPITER', 'SATURN']);
  const rows: any[] = [];
  progressedPlanets.filter((planet) => progressedKeys.has(planet.key)).forEach((progressed) => {
    natalPlanets.forEach((natal) => {
      const distance = angularDistance(progressed.longitude, natal.longitude);
      WESTERN_ASPECTS.forEach((aspect) => {
        const orb = Math.abs(distance - aspect.angle);
        const maxOrb = progressed.key === 'MOON' ? 1.5 : 1;
        if (orb <= maxOrb) {
          rows.push({
            id: `progressed-${progressed.key}-${natal.key}-${aspect.name}`,
            progressedPlanet: progressed.name,
            natalPlanet: natal.name,
            aspect: aspect.name,
            orb,
            strength: orb <= 0.25 ? 'Exact / Very Strong' : orb <= 0.75 ? 'Strong' : orb <= 1.25 ? 'Medium' : 'Weak',
            nature: aspect.nature,
            interpretation: progressedAspectInterpretation(progressed.key, natal.key, aspect.name),
          });
        }
      });
    });
  });
  return rows.sort((a, b) => a.orb - b.orb);
};

const buildProgressionGuidance = (progressedPlanets: any[], aspects: any[]) => {
  const byKey = (key: string) => progressedPlanets.find((planet) => planet.key === key);
  const moon = byKey('MOON');
  const sun = byKey('SUN');
  const mercury = byKey('MERCURY');
  const venus = byKey('VENUS');
  const mars = byKey('MARS');
  const jupiter = byKey('JUPITER');

  const life = [
    moon ? `Progressed Moon in ${moon.signDegree}, House ${moon.house}: ${progressedHouseMeaning('MOON', moon.house)}` : '',
    sun ? `Progressed Sun in ${sun.signDegree}, House ${sun.house}: ${progressedHouseMeaning('SUN', sun.house)}` : '',
    mercury ? `Progressed Mercury: thinking, communication and learning style are developing through ${mercury.signDegree}.` : '',
    venus ? `Progressed Venus: love, comfort, taste, values and money preferences are developing through ${venus.signDegree}.` : '',
    mars ? `Progressed Mars: action style, courage, ambition and drive are developing through ${mars.signDegree}.` : '',
    ...aspects.slice(0, 3).map((aspect) => `${aspect.progressedPlanet} ${aspect.aspect} natal ${aspect.natalPlanet}: ${aspect.interpretation}`),
  ].filter(Boolean);

  const career = [
    sun && ['6', '10', '11', 6, 10, 11].includes(sun.house) ? `Progressed Sun in House ${sun.house} makes work, career, responsibility or public direction more important.` : '',
    moon && ['6', '10', '11', 6, 10, 11].includes(moon.house) ? `Progressed Moon in House ${moon.house} shows emotional focus on work, career, income or public goals.` : '',
    mercury ? 'Progressed Mercury supports learning, communication, business, writing, marketing, coding or analytical work.' : '',
    mars ? 'Progressed Mars supports action, courage, competition, technical work and active career decisions.' : '',
  ].filter(Boolean);

  const love = [
    moon && ['5', '7', '8', 5, 7, 8].includes(moon.house) ? `Progressed Moon in House ${moon.house} can make love, marriage, attraction or emotional bonding more important.` : '',
    venus ? `Progressed Venus in ${venus.signDegree} shows how love style, attraction and relationship values are changing.` : '',
    mars ? `Progressed Mars in ${mars.signDegree} shows how passion, desire and assertiveness are changing.` : '',
  ].filter(Boolean);

  const money = [
    moon && ['2', '8', '11', 2, 8, 11].includes(moon.house) ? `Progressed Moon in House ${moon.house} brings emotional focus to money, savings, shared wealth or income.` : '',
    venus && ['2', '8', '11', 2, 8, 11].includes(venus.house) ? 'Progressed Venus supports financial values, comfort, gains, luxury, art or relationship-based income themes.' : '',
    jupiter ? 'Progressed Jupiter can show growth-oriented financial thinking and expansion of resources.' : '',
  ].filter(Boolean);

  return {
    life,
    career: career.length ? career : ['Career phase should be judged from Progressed Sun, Moon, Mercury, Mars and aspects to natal Sun, Saturn and Midheaven.'],
    love: love.length ? love : ['Love phase should be judged from Progressed Moon, Venus, Mars and aspects to natal Moon, Venus, Mars and the 7th house.'],
    money: money.length ? money : ['Money phase should be judged from Progressed Moon, Venus, Jupiter and natal 2nd, 8th and 11th house connections.'],
  };
};

const WesternAstrology: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentChart } = useChart();
  const { state: authState } = useAuth();
  const isPremium = hasPremiumSubscription(authState.user);
  const [chartData, setChartData] = React.useState<any>(null);
  const [activeTab, setActiveTab] = React.useState<WesternTab>('natal');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const solarReturnTarget = React.useMemo(() => getSolarReturnTarget(chartData), [chartData]);
  const progressionTarget = React.useMemo(() => getProgressionTarget(chartData), [chartData]);
  const { transitChart, loading: transitLoading, error: transitError } = useCurrentTransitOverlay(
    chartData,
    Boolean(chartData && isPremium && activeTab === 'transits')
  );
  const { transitChart: solarReturnChart, loading: solarReturnLoading, error: solarReturnError } = useCurrentTransitOverlay(
    chartData,
    Boolean(chartData && isPremium && activeTab === 'solarReturn' && solarReturnTarget),
    solarReturnTarget || undefined
  );
  const { transitChart: progressedChart, loading: progressionLoading, error: progressionError } = useCurrentTransitOverlay(
    chartData,
    Boolean(chartData && isPremium && activeTab === 'progressions' && progressionTarget),
    progressionTarget || undefined
  );

  React.useEffect(() => {
    document.title = 'Western Astrology - Kundlistar';
    if (id === 'preview') {
      setChartData(currentChart);
      setError(currentChart ? null : 'No chart data available for preview');
      setLoading(false);
      return;
    }

    const fetchChart = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/charts/${id}`);
        setChartData(response.data.data.chart);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load chart');
      } finally {
        setLoading(false);
      }
    };

    fetchChart();
  }, [id, currentChart]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !chartData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity={error ? 'error' : 'warning'}>{error || 'Chart not found'}</Alert>
      </Container>
    );
  }

  const planetRows = normalizePlanetRows(chartData);
  const cuspRows = normalizeCuspRows(chartData);
  const transitPlanetRows = normalizePlanetRows(transitChart);
  const solarReturnPlanetRows = normalizePlanetRows(solarReturnChart);
  const solarReturnCuspRows = normalizeCuspRows(solarReturnChart);
  const progressedPlanetRows = normalizePlanetRows(progressedChart).filter((planet) => ['SUN', 'MOON', 'MERCURY', 'VENUS', 'MARS', 'JUPITER', 'SATURN'].includes(planet.key));
  const progressedAspects = calculateProgressedAspects(progressedPlanetRows, planetRows);
  const natalAspects = calculateWesternAspects(labelPointSet(planetRows, 'Natal'));
  const transitAspects = calculateWesternAspects(labelPointSet(transitPlanetRows, 'Transit'), labelPointSet(planetRows, 'Natal'), false, 32);
  const solarReturnAspects = calculateWesternAspects(labelPointSet(solarReturnPlanetRows, 'Solar Return'), labelPointSet(planetRows, 'Natal'), false, 32);
  const fortune = chartData?.kpAnalysis?.partOfFortune || chartData?.partOfFortune;
  const ascendant = chartData?.houses?.ascendant || chartData?.houses?.cusps?.[1] || chartData?.houses?.cusps?.['1'];
  const midheaven = chartData?.houses?.midheaven || chartData?.houses?.cusps?.[10] || chartData?.houses?.cusps?.['10'];
  const activeMeta = WESTERN_TABS.find((tab) => tab.key === activeTab);
  const activePremiumLocked = Boolean(activeMeta?.premium && !isPremium);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <SeoHead
        title="Western Astrology Module | Kundlistar"
        description="Explore Western Astrology inside Kundlistar with natal chart positions, Placidus house cusps, outer planets and premium Transits, Solar Return and Synastry features."
        keywords="Western astrology, natal chart, transits, solar return, synastry, Kundlistar"
      />
      <AnalysisTabBar active="western" chartId={id || chartData?._id || 'preview'} />

      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box>
              <Chip icon={<PublicIcon />} label="New section" color="primary" sx={{ mb: 1 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                Western Astrology
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 820 }}>
                Natal Western chart view with dedicated premium tabs for Transits, Solar Return and Synastry.
              </Typography>
            </Box>
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">Loaded chart</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{chartData?.birthDetails?.name || 'Chart'}</Typography>
              <Typography variant="body2" color="text.secondary">{chartData?.birthDetails?.placeOfBirth?.city || '-'}</Typography>
              {isPremium ? <Chip size="small" color="success" label="Premium active" /> : null}
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
          >
            {WESTERN_TABS.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{tab.label}</span>
                    {tab.premium && !isPremium ? <LockIcon fontSize="small" color="warning" /> : null}
                  </Stack>
                }
              />
            ))}
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {activePremiumLocked ? (
              <PremiumLockedPanel
                title={`Unlock ${activeMeta?.label || 'Western Astrology'}`}
                message={`${activeMeta?.label || 'This Western Astrology layer'} is available after upgrade.`}
                onUpgrade={() => navigate('/upgrade')}
              />
            ) : null}
            {!activePremiumLocked && activeTab === 'natal' ? (
              <NatalWesternTab
                chartData={chartData}
                planetRows={planetRows}
                cuspRows={cuspRows}
                ascendant={ascendant}
                midheaven={midheaven}
                fortune={fortune}
                natalAspects={natalAspects}
              />
            ) : null}
            {!activePremiumLocked && activeTab === 'transits' ? (
              <TransitWesternTab
                chartData={chartData}
                planetRows={planetRows}
                cuspRows={cuspRows}
                transitPlanetRows={transitPlanetRows}
                transitAspects={transitAspects}
                loading={transitLoading}
                error={transitError}
              />
            ) : null}
            {!activePremiumLocked && activeTab === 'solarReturn' ? (
              <SolarReturnWesternTab
                chartData={chartData}
                planetRows={planetRows}
                natalAspects={natalAspects}
                solarReturnChart={solarReturnChart}
                solarReturnPlanetRows={solarReturnPlanetRows}
                solarReturnCuspRows={solarReturnCuspRows}
                solarReturnAspects={solarReturnAspects}
                solarReturnTarget={solarReturnTarget}
                loading={solarReturnLoading}
                error={solarReturnError}
              />
            ) : null}
            {!activePremiumLocked && activeTab === 'synastry' ? (
              <SynastryWesternTab
                chartData={chartData}
                natalAspects={natalAspects}
              />
            ) : null}
            {!activePremiumLocked && activeTab === 'progressions' ? (
              <ProgressionsWesternTab
                chartData={chartData}
                progressedChart={progressedChart}
                progressedPlanets={progressedPlanetRows}
                progressedAspects={progressedAspects}
                progressionTarget={progressionTarget}
                loading={progressionLoading}
                error={progressionError}
              />
            ) : null}
          </Box>
        </Paper>

        <Alert severity="info">
          Western Astrology is provided for astrological insight and self-reflection. It is guidance, not a guarantee or replacement for professional advice.
        </Alert>
      </Stack>
    </Container>
  );
};

const NatalWesternTab: React.FC<{
  chartData: any;
  planetRows: ReturnType<typeof normalizePlanetRows>;
  cuspRows: ReturnType<typeof normalizeCuspRows>;
  ascendant: any;
  midheaven: any;
  fortune: any;
  natalAspects: any[];
}> = ({ chartData, planetRows, cuspRows, ascendant, midheaven, fortune, natalAspects }) => (
  <Stack spacing={3}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 0.85fr) minmax(0, 1.15fr)' }, gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Natal Western Chart</Typography>
          <WesternZodiacWheel chartData={chartData} planetRows={planetRows} cuspRows={cuspRows} />
        </CardContent>
      </Card>

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Natal Points</Typography>
            <Stack spacing={1.2}>
              <InfoRow label="Ascendant" value={formatSignDegree(ascendant)} />
              <InfoRow label="Midheaven" value={formatSignDegree(midheaven)} />
              <InfoRow label="House System" value="Placidus cusps" />
              <InfoRow label="Part of Fortune" value={fortune ? formatDegree(fortune.longitude) : '-'} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>

    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Planet</TableCell>
            <TableCell>Sign & Degree</TableCell>
            <TableCell>House</TableCell>
            <TableCell>Longitude</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {planetRows.map((planet) => (
            <TableRow key={planet.key}>
              <TableCell sx={{ fontWeight: 600 }}>{planet.name}</TableCell>
              <TableCell>{planet.signDegree}</TableCell>
              <TableCell>{planet.house}</TableCell>
              <TableCell>{planet.longitudeText}</TableCell>
              <TableCell>{planet.retrograde ? 'Retrograde' : 'Direct'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>House</TableCell>
            <TableCell>Placidus Cusp</TableCell>
            <TableCell>Longitude</TableCell>
            <TableCell>Sign Ruler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cuspRows.map((cusp) => (
            <TableRow key={cusp.house}>
              <TableCell sx={{ fontWeight: 600 }}>House {cusp.house}</TableCell>
              <TableCell>{cusp.signDegree}</TableCell>
              <TableCell>{cusp.longitudeText}</TableCell>
              <TableCell>{cusp.ruler}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <WesternAspectTable
      title="Natal Western Aspects"
      aspects={natalAspects}
      emptyText="No major Western aspects found within the configured orbs."
    />
  </Stack>
);

const WesternZodiacWheel: React.FC<{
  chartData: any;
  planetRows: ReturnType<typeof normalizePlanetRows>;
  cuspRows: ReturnType<typeof normalizeCuspRows>;
  transitPlanetRows?: ReturnType<typeof normalizePlanetRows>;
}> = ({ chartData, planetRows, cuspRows, transitPlanetRows = [] }) => {
  const size = 420;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.46;
  const signR = size * 0.38;
  const houseR = size * 0.30;
  const hasTransitOverlay = transitPlanetRows.length > 0;
  const natalPlanetR = hasTransitOverlay ? size * 0.34 : size * 0.42;
  const transitPlanetR = size * 0.445;
  const ascLongitude = Number(chartData?.houses?.ascendant?.longitude ?? cuspRows.find((c) => c.house === '1')?.longitude ?? 0);
  const mcLongitude = Number(chartData?.houses?.midheaven?.longitude ?? cuspRows.find((c) => c.house === '10')?.longitude);

  const angleForLongitude = (longitude: number) => ((longitude - ascLongitude + 360) % 360) + 270;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Western natal astrology wheel">
        <circle cx={cx} cy={cy} r={outerR} fill="#f8fafc" stroke="#2e7d52" strokeWidth={2} />
        {hasTransitOverlay ? (
          <circle cx={cx} cy={cy} r={size * 0.415} fill="none" stroke="#f97316" strokeWidth={1.4} strokeDasharray="5 5" opacity={0.8} />
        ) : null}
        <circle cx={cx} cy={cy} r={signR} fill="#ffffff" stroke="#86efac" strokeWidth={1.2} />
        <circle cx={cx} cy={cy} r={houseR} fill="#f0fdf4" stroke="#bbf7d0" strokeWidth={1} />

        {Array.from({ length: 12 }, (_, index) => {
          const boundary = angleForLongitude(index * 30);
          const next = angleForLongitude(index * 30 + 30);
          const line = polarPoint(cx, cy, outerR, boundary);
          const inner = polarPoint(cx, cy, houseR * 0.52, boundary);
          const label = polarPoint(cx, cy, signR, angleForLongitude(index * 30 + 15));
          return (
            <g key={`sign-${index}`}>
              <line x1={inner.x} y1={inner.y} x2={line.x} y2={line.y} stroke="#15803d" strokeWidth={1} opacity={0.75} />
              <text x={label.x} y={label.y} textAnchor="middle" dominantBaseline="middle" fontSize={12} fill="#166534" fontWeight={600}>
                {SIGN_GLYPHS[index]}
              </text>
              <path
                d={`M ${polarPoint(cx, cy, outerR, boundary).x} ${polarPoint(cx, cy, outerR, boundary).y} A ${outerR} ${outerR} 0 0 1 ${polarPoint(cx, cy, outerR, next).x} ${polarPoint(cx, cy, outerR, next).y}`}
                fill="none"
                stroke="#dcfce7"
                strokeWidth={4}
              />
            </g>
          );
        })}

        {cuspRows.map((cusp) => {
          const angle = angleForLongitude(cusp.longitude);
          const p1 = polarPoint(cx, cy, houseR * 0.52, angle);
          const p2 = polarPoint(cx, cy, outerR, angle);
          const label = polarPoint(cx, cy, houseR * 0.74, angle + 4);
          return (
            <g key={`cusp-${cusp.house}`}>
              <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#64748b" strokeWidth={cusp.house === '1' || cusp.house === '10' ? 2 : 0.8} opacity={0.85} />
              <text x={label.x} y={label.y} textAnchor="middle" dominantBaseline="middle" fontSize={9} fill="#475569" fontWeight={600}>
                {cusp.house}
              </text>
            </g>
          );
        })}

        {planetRows.map((planet, index) => {
          const angle = angleForLongitude(planet.longitude);
          const clusterOffset = ((index % 4) - 1.5) * 8;
          const point = polarPoint(cx, cy, natalPlanetR + clusterOffset, angle);
          const color = PLANET_COLORS[planet.key] || '#1f2937';
          return (
            <g key={`planet-${planet.key}`}>
              <circle cx={point.x} cy={point.y} r={11} fill="#fff" stroke={color} strokeWidth={1.4} />
              <text x={point.x} y={point.y + 0.5} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill={color} fontWeight={600}>
                {PLANET_ABBR[planet.key] || planet.name.slice(0, 2)}
              </text>
            </g>
          );
        })}

        {transitPlanetRows.map((planet, index) => {
          const angle = angleForLongitude(planet.longitude);
          const clusterOffset = ((index % 4) - 1.5) * 6;
          const point = polarPoint(cx, cy, transitPlanetR + clusterOffset, angle);
          const color = PLANET_COLORS[planet.key] || '#f97316';
          return (
            <g key={`transit-planet-${planet.key}`}>
              <circle cx={point.x} cy={point.y} r={12} fill="#fff7ed" stroke="#f97316" strokeWidth={2} />
              <text x={point.x} y={point.y + 0.5} textAnchor="middle" dominantBaseline="middle" fontSize={8.5} fill={color} fontWeight={600}>
                T-{PLANET_ABBR[planet.key] || planet.name.slice(0, 2)}
              </text>
            </g>
          );
        })}

        <text x={cx} y={cy - 10} textAnchor="middle" fontSize={18} fill="#166534" fontWeight={600}>ASC</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize={12} fill="#166534" fontWeight={600}>{SIGNS[Math.floor(ascLongitude / 30)] || 'Ascendant'}</text>
        {hasTransitOverlay ? (
          <text x={cx} y={cy + 48} textAnchor="middle" fontSize={10.5} fill="#ea580c" fontWeight={600}>
            Outer ring: Transit over Natal
          </text>
        ) : null}
        {Number.isFinite(mcLongitude) ? (
          <text x={cx} y={cy + 30} textAnchor="middle" fontSize={11} fill="#475569" fontWeight={600}>
            MC {formatDegree(mcLongitude)}
          </text>
        ) : null}
      </svg>
    </Box>
  );
};

const PremiumLockedPanel: React.FC<{ title: string; message: string; onUpgrade: () => void }> = ({ title, message, onUpgrade }) => (
  <Box
    sx={{
      border: '1px dashed',
      borderColor: 'warning.main',
      borderRadius: 2,
      p: 3,
      textAlign: 'center',
      bgcolor: "var(--app-surface-warning)",
      color: 'warning.contrastText',
    }}
  >
    <LockIcon sx={{ fontSize: 42, mb: 1 }} />
    <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" sx={{ my: 1.5 }}>{message}</Typography>
    <Button variant="contained" color="warning" onClick={onUpgrade}>
      Upgrade
    </Button>
  </Box>
);

const PremiumWesternTab: React.FC<{ title: string; description: string; rows: string[][]; children?: React.ReactNode }> = ({ title, description, rows, children }) => (
  <Stack spacing={2}>
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>
    </Paper>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell sx={{ fontWeight: 600, width: 220 }}>{label}</TableCell>
              <TableCell>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {children}
  </Stack>
);

const TransitWesternTab: React.FC<{
  chartData: any;
  planetRows: any[];
  cuspRows: any[];
  transitPlanetRows: any[];
  transitAspects: any[];
  loading: boolean;
  error: string | null;
}> = ({ chartData, planetRows, cuspRows, transitPlanetRows, transitAspects, loading, error }) => (
  <PremiumWesternTab
    title="Western Transits"
    description="Current transit-to-natal calculations using the loaded chart birthplace."
    rows={[
      ['Natal points available', `${planetRows.length} planets + ${cuspRows.length} Placidus cusps`],
      ['Transit planets loaded', loading ? 'Loading current transit positions...' : `${transitPlanetRows.length} current planets`],
      ['Aspect method', 'Conjunction, sextile, square, trine and opposition with Western orbs'],
      ['Primary focus', 'Transit contacts to natal Sun, Moon, Ascendant, Venus, Mars, Jupiter, Saturn and outer planets'],
    ]}
  >
    {error ? <Alert severity="warning">{error}</Alert> : null}
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Transit Over Natal Double Wheel</Typography>
        <WesternZodiacWheel
          chartData={chartData}
          planetRows={planetRows}
          cuspRows={cuspRows}
          transitPlanetRows={transitPlanetRows}
        />
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label="Natal inner wheel" color="primary" />
          <Chip size="small" label="Transit outer wheel" sx={{ bgcolor: "var(--app-surface-orange, #fff7ed)", color: "var(--app-ink-orange, #c2410c)", border: "1px solid var(--app-border, #fed7aa)" }} />
        </Stack>
      </CardContent>
    </Card>
    <WesternPlanetTable title="Current Transit Positions" planets={transitPlanetRows} emptyText={loading ? 'Loading current transit positions...' : 'Transit positions are not available.'} />
    <WesternAspectTable title="Current Transit Aspects to Natal Chart" aspects={transitAspects} emptyText={loading ? 'Loading transit aspects...' : 'No major transit aspects found within the configured orbs.'} />
  </PremiumWesternTab>
);

const SolarReturnWesternTab: React.FC<{
  chartData: any;
  planetRows: any[];
  natalAspects: any[];
  solarReturnChart: any;
  solarReturnPlanetRows: any[];
  solarReturnCuspRows: any[];
  solarReturnAspects: any[];
  solarReturnTarget: { date: string; time: string } | null;
  loading: boolean;
  error: string | null;
}> = ({ chartData, planetRows, natalAspects, solarReturnChart, solarReturnPlanetRows, solarReturnCuspRows, solarReturnAspects, solarReturnTarget, loading, error }) => {
  const sun = planetRows.find((planet) => planet.key === 'SUN');
  const sunAspects = natalAspects.filter((aspect) => `${aspect.pointA} ${aspect.pointB}`.toLowerCase().includes('sun'));

  return (
    <PremiumWesternTab
      title="Solar Return"
      description="Annual Western return reference from the natal Sun, with supporting natal aspect context."
      rows={[
        ['Solar return date', formatSolarReturnDate(chartData)],
        ['Calculation time', solarReturnTarget ? `${solarReturnTarget.date} ${solarReturnTarget.time}` : 'Birth date not available'],
        ['Natal Sun', sun?.signDegree || '-'],
        ['Natal Sun house', sun?.house ? `House ${sun.house}` : '-'],
        ['Return planets loaded', loading ? 'Loading solar return chart...' : `${solarReturnPlanetRows.length} planets + ${solarReturnCuspRows.length} cusps`],
        ['Annual focus', sun?.house ? `Solar themes are read through natal Sun in House ${sun.house}, return Ascendant, house emphasis and return-to-natal aspects.` : 'Solar themes are read from the natal Sun, return Ascendant and yearly return chart.'],
      ]}
    >
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {solarReturnChart ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Solar Return Chart</Typography>
            <WesternZodiacWheel chartData={solarReturnChart} planetRows={solarReturnPlanetRows} cuspRows={solarReturnCuspRows} />
          </CardContent>
        </Card>
      ) : null}
      <WesternPlanetTable title="Solar Return Planet Positions" planets={solarReturnPlanetRows} emptyText={loading ? 'Loading solar return planets...' : 'Solar return positions are not available.'} />
      <WesternCuspTable title="Solar Return House Cusps" cusps={solarReturnCuspRows} emptyText={loading ? 'Loading solar return cusps...' : 'Solar return house cusps are not available.'} />
      <WesternAspectTable title="Solar Return Aspects to Natal Chart" aspects={solarReturnAspects} emptyText={loading ? 'Loading solar return aspects...' : 'No major solar return aspects found within the configured orbs.'} />
      <WesternAspectTable title="Natal Sun Aspects for Solar Return Focus" aspects={sunAspects} emptyText="No major natal Sun aspects found within the configured orbs." />
    </PremiumWesternTab>
  );
};

const SynastryWesternTab: React.FC<{ chartData: any; natalAspects: any[] }> = ({ chartData, natalAspects }) => (
  <PremiumWesternTab
    title="Synastry"
    description="Relationship overlay framework using planet-to-planet aspects, angle contacts and house overlays."
    rows={[
      ['Loaded first chart', chartData?.birthDetails?.name || 'Chart'],
      ['Second chart', 'Select or load partner chart to calculate full synastry overlay'],
      ['Aspect method', 'Major inter-chart aspects with tight orb priority'],
      ['House overlay method', 'Partner planets are placed into the first chart houses after second chart is selected'],
    ]}
  >
    <WesternAspectTable
      title="Relationship Pattern Aspects from Loaded Chart"
      aspects={relationshipPoints(natalAspects)}
      emptyText="No major relationship-focused natal aspects found within the configured orbs."
    />
  </PremiumWesternTab>
);

const ProgressionsWesternTab: React.FC<{
  chartData: any;
  progressedChart: any;
  progressedPlanets: any[];
  progressedAspects: any[];
  progressionTarget: any;
  loading: boolean;
  error: string | null;
}> = ({ chartData, progressedChart, progressedPlanets, progressedAspects, progressionTarget, loading, error }) => {
  const moon = progressedPlanets.find((planet) => planet.key === 'MOON');
  const moonPhase = getProgressedMoonPhase(progressedPlanets);
  const guidance = buildProgressionGuidance(progressedPlanets, progressedAspects);

  return (
    <PremiumWesternTab
      title="Life Phase Progression"
      description="Understand your current inner life phase through Western Secondary Progressions."
      rows={[
        ['Method', 'Secondary Progression'],
        ['Formula', '1 day after birth = 1 year of life'],
        ['Current age used', progressionTarget ? `${progressionTarget.ageYears.toFixed(2)} years` : 'Birth date not available'],
        ['Progressed date', progressionTarget ? `${progressionTarget.date} ${progressionTarget.time}` : '-'],
        ['Progressed Moon', moon ? `${moon.signDegree}, House ${moon.house}` : loading ? 'Loading progressed Moon...' : '-'],
        ['Progressed Moon phase', moonPhase ? `${moonPhase.phase} (${moonPhase.angle.toFixed(2)} deg)` : loading ? 'Loading Moon phase...' : '-'],
      ]}
    >
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {progressedChart ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Progressed Western Chart</Typography>
            <WesternZodiacWheel chartData={progressedChart} planetRows={progressedPlanets} cuspRows={normalizeCuspRows(progressedChart)} />
          </CardContent>
        </Card>
      ) : null}
      {moonPhase ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>Progressed Moon Phase</Typography>
          <Typography sx={{ mt: 0.5, fontWeight: 600 }}>{moonPhase.phase}</Typography>
          <Typography variant="body2" color="text.secondary">{moonPhase.meaning}</Typography>
        </Paper>
      ) : null}
      <ProgressedPlanetMeaningTable planets={progressedPlanets} loading={loading} />
      <ProgressedAspectTable aspects={progressedAspects} loading={loading} />
      <ProgressionGuidanceSection title="Current Life Phase" points={guidance.life} fallback="Secondary progressions show the inner phase of life, emotional maturity, identity development and changing priorities." />
      <ProgressionGuidanceSection title="Progressed Career Phase" points={guidance.career} />
      <ProgressionGuidanceSection title="Progressed Love Phase" points={guidance.love} />
      <ProgressionGuidanceSection title="Progressed Money Phase" points={guidance.money} />
    </PremiumWesternTab>
  );
};

const ProgressedPlanetMeaningTable: React.FC<{ planets: any[]; loading: boolean }> = ({ planets, loading }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={5} sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>Progressed Planets With Meaning</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Planet</TableCell>
          <TableCell>Sign & Degree</TableCell>
          <TableCell>House</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Prediction Meaning</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {planets.length ? planets.map((planet) => (
          <TableRow key={planet.key}>
            <TableCell sx={{ fontWeight: 600 }}>{planet.name}</TableCell>
            <TableCell>{planet.signDegree}</TableCell>
            <TableCell>{planet.house}</TableCell>
            <TableCell>{planet.retrograde ? 'Retrograde' : 'Direct'}</TableCell>
            <TableCell>{progressedHouseMeaning(planet.key, planet.house)}</TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={5}>{loading ? 'Loading progressed planets...' : 'Progressed planets are not available.'}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const ProgressedAspectTable: React.FC<{ aspects: any[]; loading: boolean }> = ({ aspects, loading }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={6} sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>Progressed-to-Natal Aspects</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Progressed</TableCell>
          <TableCell>Aspect</TableCell>
          <TableCell>Natal</TableCell>
          <TableCell>Orb</TableCell>
          <TableCell>Strength</TableCell>
          <TableCell>Prediction Meaning</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {aspects.length ? aspects.map((aspect) => (
          <TableRow key={aspect.id}>
            <TableCell sx={{ fontWeight: 600 }}>{aspect.progressedPlanet}</TableCell>
            <TableCell>{aspect.aspect}</TableCell>
            <TableCell>{aspect.natalPlanet}</TableCell>
            <TableCell>{aspect.orb.toFixed(2)} deg</TableCell>
            <TableCell>{aspect.strength} · {aspect.nature}</TableCell>
            <TableCell>{aspect.interpretation}</TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={6}>{loading ? 'Loading progressed aspects...' : 'No tight progressed-to-natal aspects found right now.'}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const ProgressionGuidanceSection: React.FC<{ title: string; points: string[]; fallback?: string }> = ({ title, points, fallback }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)", mb: 1 }}>{title}</Typography>
    <Stack spacing={1}>
      {(points.length ? points : [fallback || 'This phase should be judged from progressed planets, progressed Moon phase and tight progressed-to-natal aspects.']).map((point) => (
        <Typography key={point} variant="body2" color="text.secondary">{point}</Typography>
      ))}
    </Stack>
  </Paper>
);

const WesternPlanetTable: React.FC<{ title: string; planets: any[]; emptyText: string }> = ({ title, planets, emptyText }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={5} sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{title}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Planet</TableCell>
          <TableCell>Sign & Degree</TableCell>
          <TableCell>House</TableCell>
          <TableCell>Longitude</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {planets.length ? planets.map((planet) => (
          <TableRow key={planet.key}>
            <TableCell sx={{ fontWeight: 600 }}>{planet.name}</TableCell>
            <TableCell>{planet.signDegree}</TableCell>
            <TableCell>{planet.house}</TableCell>
            <TableCell>{planet.longitudeText}</TableCell>
            <TableCell>{planet.retrograde ? 'Retrograde' : 'Direct'}</TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={5}>{emptyText}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const WesternCuspTable: React.FC<{ title: string; cusps: any[]; emptyText: string }> = ({ title, cusps, emptyText }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={4} sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{title}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>House</TableCell>
          <TableCell>Placidus Cusp</TableCell>
          <TableCell>Longitude</TableCell>
          <TableCell>Sign Ruler</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {cusps.length ? cusps.map((cusp) => (
          <TableRow key={cusp.house}>
            <TableCell sx={{ fontWeight: 600 }}>House {cusp.house}</TableCell>
            <TableCell>{cusp.signDegree}</TableCell>
            <TableCell>{cusp.longitudeText}</TableCell>
            <TableCell>{cusp.ruler}</TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={4}>{emptyText}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const WesternAspectTable: React.FC<{ title: string; aspects: any[]; emptyText: string }> = ({ title, aspects, emptyText }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell colSpan={5} sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{title}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Aspect</TableCell>
          <TableCell>Point 1</TableCell>
          <TableCell>Point 2</TableCell>
          <TableCell>Orb</TableCell>
          <TableCell>Strength</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {aspects.length ? aspects.map((aspect) => (
          <TableRow key={aspect.id}>
            <TableCell sx={{ fontWeight: 600 }}>{aspect.aspect}</TableCell>
            <TableCell>{aspect.pointA}</TableCell>
            <TableCell>{aspect.pointB}</TableCell>
            <TableCell>{aspect.orb.toFixed(2)} deg</TableCell>
            <TableCell>{aspect.strength} · {aspect.nature}</TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={5}>{emptyText}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
  </Box>
);

export default WesternAstrology;
